import { SORT_DIRECTIONS } from 'app/shared/componentsRestyled/common/TooltipedSortLabel';
import { PinnedPosition } from 'app/shared/componentsRestyled/coreDesignSystem/form/AutocompleteCDS/AutocompleteCDS.constants';
import _ from 'lodash';
import { views } from 'react-big-calendar/lib/utils/constants';

import { CustomQuestionsAnswerType } from 'constants/customQuestions';
import { LoanRoleCapacity } from 'constants/loanRoles';

import { FANNIE_SYSTEM_TYPE } from './aus';
import {
  CompanyType,
  FederalAgencyComplianceType,
  HmdaFederalAgencyCode,
  LoanNumberResetBehavior,
  LoanNumberSegmentType,
  OrganizationType,
  PpeType,
  SubCompanyType,
} from './company';
import { FeeProviderPaidToType } from './feeProviders';
import {
  DisclosureType,
  DynamicFieldPostfixSymbol,
  DynamicFieldPrefixSymbol,
  FormApplicationType,
  FormPurpose,
  FormPurposeType,
  HorizontalAlignmentType,
  VerticalAlignmentType,
} from './forms';
import { ImpoundCategoryType } from './impoundTypes';
import {
  AdditionalLoanSecondMortgageType,
  AsianRaceSubType,
  AssetsRelationshipType,
  AssetsType,
  AttachmentType,
  BankruptType,
  BaseStatus,
  BorrowerHomeownershipEducationFormat,
  BorrowerHousingCounselingFormat,
  BranchOfServiceType,
  BusinessVerificationMethod,
  ChattelHomeCondition,
  ChattelValueSource,
  CitizenshipStatusType,
  ClosingDocumentType,
  ClosingInTrustType,
  ClosingLocation,
  CreditRatingStatus,
  CreditTypeEnum,
  DeedRestrictionType,
  DeferredType,
  DeliveryStatus,
  DemographicInformationProvisionType,
  DownPaymentSourceType,
  EmployedAtTimeOfVerification,
  EmployerPhoneSource,
  EntitlementCodeFromCoeType,
  EscrowRequirementType,
  EthnicityType,
  FrequencyType,
  FundingInformationType,
  GovernmentRefinancePurposeType,
  HispanicOrLatinoEthnicitySubType,
  HousingType,
  IncomeDocumentationType,
  IncomeType,
  LanguagePreferenceV2,
  LESection,
  LESectionCDType,
  LiabilitiesType,
  LiabilityExpenseType,
  LienPriorityType,
  LoanPurposeType,
  LoanType,
  LoanTypePurposeType,
  LocationType,
  MannerHeldType,
  ManufacturedHomeType,
  MaritalStatusType,
  MAX_COBORROWER_NUMBER,
  MAX_NONBORROWER_NUMBER,
  MiApplicationType,
  MiCompanyType,
  MiDecisionType,
  MilitaryStatusType,
  MiOrderStatus,
  MiPaymentType,
  MiProductType,
  MiRenewalType,
  NationalHousingActSection,
  NativeHawaiianOrOtherRaceSubType,
  OccupancyType,
  PaymentExclusionReasonType,
  PrepaymentPenaltyStructure,
  PricingWorkbenchPropertyType,
  ProbabilityOfContinuedEmployment,
  ProjectClassification,
  ProjectInformation,
  PropertyRightsEstateHeld,
  PropertyStatusType,
  PropertyType,
  PurchaseCreditSourceType,
  PurchaseCreditType,
  PurposeOfChattel,
  RefinancePurposeType,
  RentCalculatedPurposeType,
  SalaryType,
  ServicingType,
  SitePlacement,
  SourceOfFundsType,
  SourceTypeTypes,
  SuffixType,
  TangibleNetBenefitReasonType,
  TaxFilingType,
  TitleHoldingType,
  TitlePerTaxReturnType,
  TransactionDetailClosingType,
  TransactionDetailType,
  TypeOfCredit,
  UccpAppraisalUploadStatus,
  VerificationMethod,
} from './loanApplication';
import { LoanContactTypeEnum } from './loanContactTypes';
import { LoanFeePaymentTimeEnum } from './loanFee';
import { MismoDocumentType } from './mismo';
import { Sex, SEX_NOT_PROVIDED_VALUE } from './profile';
import {
  AmortizationType,
  AssumableDetailsOption,
  DocMagicPlanCodeType,
  EXECUTION_TYPE,
  FormPopulationOption,
  LoanProgramAmortizationType,
  MinimumInitialDrawType,
  OriginationFeeType,
  PrepaymentPenaltyFeatureType,
  RateType,
} from './programs';
import { buildLoanTypeOptions, PROGRAM_LOAN_TYPES } from './programType';
import states, { allStatesWithMilitaryAddresses, allStatesWithNationalCategory } from './states';
import { EntityAssignmentType } from './taskCategories';
import { TaskStatus } from './tasks';
import { UcdSubmitType } from './ucd';

const MONTH_OPTIONS_UPPER_LIMIT = 11;
const MONTH_OPTIONS_COUNT = 13;

export const refinancePurposeOptions = [
  { name: 'Pay Off Credit Cards', value: RefinancePurposeType.PAYOFF_CREDIT_CARDS },
  { name: 'Take Cash Out Of Home', value: RefinancePurposeType.TAKE_CASH_OUT_OF_HOME },
  { name: 'Lower Your Monthly Payment', value: RefinancePurposeType.LOWER_MONTHLY_PAYMENT },
  { name: 'Pay Off Loan Faster', value: RefinancePurposeType.PAYOFF_LOAN_FASTER },
  { name: 'Consolidate Loans', value: RefinancePurposeType.CONSOLIDATE_LOANS },
  { name: 'Home Improvement', value: RefinancePurposeType.HOME_IMPROVEMENT },
  { name: 'FHA Simple Refinance', value: RefinancePurposeType.FHA_SIMPLE_REFINANCE },
  { name: 'FHA Streamline Refinance', value: RefinancePurposeType.FHA_STREAMLINE_REFINANCE },
  {
    name: 'VA Interest Rate Reduction Refinance Loan',
    value: RefinancePurposeType.VA_RATE_REDUCTION,
  },
];

export const tangibleNetBenefitReasonOptions = [
  { name: 'Avoiding foreclosure', value: TangibleNetBenefitReasonType.AVOIDING_FORECLOSURE },
  {
    name: 'Beneficial change in amortization period',
    value: TangibleNetBenefitReasonType.BENEFICIAL_CHANGE_IN_AMORTIZATION_PERIOD,
  },
  {
    name: 'Bona fide personal need or important use of proceeds',
    value: TangibleNetBenefitReasonType.BONA_FIDE_PERSONAL_NEED,
  },
  {
    name: 'Change from adjustable rate to fixed rate',
    value: TangibleNetBenefitReasonType.CHANGE_FROM_ADJUSTABLE_TO_FIXED_RATE,
  },
  {
    name: 'Consolidation of other loans or debt',
    value: TangibleNetBenefitReasonType.CONSOLIDATION_OF_DEBT,
  },
  {
    name: 'Eliminating a balloon payment feature',
    value: TangibleNetBenefitReasonType.ELIMINATING_BALLOON_PAYMENT,
  },
  {
    name: 'Eliminating negative amortization',
    value: TangibleNetBenefitReasonType.ELIMINATING_NEGATIVE_AMORTIZATION,
  },
  {
    name: 'Eliminating private mortgage insurance',
    value: TangibleNetBenefitReasonType.ELIMINATING_PRIVATE_MORTGAGE_INSURANCE,
  },
  { name: 'Lower interest rate', value: TangibleNetBenefitReasonType.LOWER_INTEREST_RATE },
  { name: 'Lower monthly payment', value: TangibleNetBenefitReasonType.LOWER_MONTHLY_PAYMENT },
  {
    name: 'Receipt of cash in excess of closing costs',
    value: TangibleNetBenefitReasonType.RECEIPT_OF_CASH,
  },
  { name: 'Other, please specify', value: TangibleNetBenefitReasonType.OTHER },
];

export const transactionDetailOptions = [
  {
    name: 'Conversion of Contract for Deed or Land Contract',
    value: TransactionDetailType.CONVERSATION_OF_CONTRACT_FOR_DEED_OR_LAND_CONTRACT,
  },
  { name: 'Renovation', value: TransactionDetailType.RENOVATION },
  {
    name: 'Construction-Conversion/Construction-to-Permanent',
    value: TransactionDetailType.CONSTRUCTION_CONVERSATION_OR_TO_PERMANENT,
  },
];

export const sourceOfFundsOptions = [
  { name: 'Community Non-Profit', value: SourceOfFundsType.COMMUNITY_NON_PROFIT },
  { name: 'Employer', value: SourceOfFundsType.EMPLOYER },
  { name: 'Federal Agency', value: SourceOfFundsType.FEDERAL_AGENCY },
  { name: 'Lender', value: SourceOfFundsType.LENDER },
  { name: 'Local Agency', value: SourceOfFundsType.LOCAL_AGENCY },
  { name: 'Other', value: SourceOfFundsType.OTHER },
  { name: 'Parent', value: SourceOfFundsType.PARENT },
  { name: 'Property Seller', value: SourceOfFundsType.PROPERTY_SELLER },
  { name: 'Relative', value: SourceOfFundsType.RELATIVE },
  { name: 'Religious Non-Profit', value: SourceOfFundsType.RELIGIOUS_NON_PROFIT },
  { name: 'State Agency', value: SourceOfFundsType.STATE_AGENCY },
  { name: 'Unmarried Partner', value: SourceOfFundsType.UNMARRIED_PARTNER },
  { name: 'Unrelated Friend', value: SourceOfFundsType.UNRELATED_FRIEND },
];

export const paymentExclusionReasonOptions = [
  { name: 'Co-signed Loan', value: PaymentExclusionReasonType.CO_SIGNED_LOAN },
  { name: 'Court Ordered', value: PaymentExclusionReasonType.COURT_ORDERED },
  { name: 'Deferred', value: PaymentExclusionReasonType.DEFERRED },
  { name: 'Duplicate', value: PaymentExclusionReasonType.DUPLICATE },
  { name: 'Less Than 10 Mths Remain', value: PaymentExclusionReasonType.LESS_THAN_10_MTHS_REMAIN },
  { name: "Not the Borrower's", value: PaymentExclusionReasonType.NOT_THE_BORROWERS },
  { name: 'Other', value: PaymentExclusionReasonType.OTHER },
  { name: 'Paid by Employer', value: PaymentExclusionReasonType.PAID_BY_EMPLOYER },
  { name: 'Paid by Others', value: PaymentExclusionReasonType.PAID_BY_OTHERS },
  { name: 'Paid in Full', value: PaymentExclusionReasonType.PAID_IN_FULL },
  { name: 'Property Buyout', value: PaymentExclusionReasonType.PROPERTY_BUYOUT },
  { name: 'Secured', value: PaymentExclusionReasonType.SECURED },
  { name: 'To Be Paid', value: PaymentExclusionReasonType.TO_BE_PAID },
];

export const lienPriorityOptions = [
  { name: 'First', value: LienPriorityType.FIRST, pinned: PinnedPosition.TOP },
  { name: 'Second', value: LienPriorityType.SECOND, pinned: PinnedPosition.TOP },
  { name: 'Third', value: LienPriorityType.THIRD, pinned: PinnedPosition.TOP },
  { name: 'Fourth', value: LienPriorityType.FOURTH, pinned: PinnedPosition.TOP },
  { name: 'Other', value: LienPriorityType.OTHER, pinned: PinnedPosition.BOTTOM },
];

export const deferredOptions = [
  { name: 'Deferred Fully Forgiven', value: DeferredType.DEFERRED_FULLY_FORGIVEN },
  { name: 'Deferred Not Fully Forgiven', value: DeferredType.DEFERRED_NOT_FULLY_FORGIVEN },
];

export const transactionDetailClosingTypeOptions = [
  {
    name: 'Single-Closing',
    value: TransactionDetailClosingType.SINGLE_CLOSING,
  },
  { name: 'Two-Closing', value: TransactionDetailClosingType.TWO_CLOSING },
];

export const loanPurposeOptions = [
  { name: 'Purchase', value: LoanPurposeType.PURCHASE },
  { name: 'Refinance', value: LoanPurposeType.REFINANCE },
  { name: 'Subordinate Financing', value: LoanPurposeType.ADDITIONAL_LOAN },
  { name: 'Chattel / Manufactured Home', value: LoanPurposeType.CHATTEL_LOAN },
];

export const loanPurposeOptionsMap = _.keyBy(loanPurposeOptions, 'value');

export const additionalLoanSecondMortgageOptions = [
  { name: 'Second Mortgage', value: AdditionalLoanSecondMortgageType.SECOND_MORTGAGE },
  { name: 'HELOC', value: AdditionalLoanSecondMortgageType.HELOC },
];

export const loanTypePurposeOptions = [
  { name: 'BPL', value: LoanTypePurposeType.BPL },
  { name: 'Residential', value: LoanTypePurposeType.RESIDENTIAL },
];

export const miProductTypeOptions = [
  { name: 'Monthly', value: MiProductType.MONTHLY },
  { name: 'Single Premium', value: MiProductType.SINGLE_PREMIUM },
  { name: 'Split Premium', value: MiProductType.SPLIT_PREMIUM },
  { name: 'Prepaid Monthly', value: MiProductType.PREPAID_MONTHLY },
];

export const miPaymentTypeOptions = [
  { name: 'Borrower-Paid', value: MiPaymentType.BORROWER_PAID },
  { name: 'Lender-Paid', value: MiPaymentType.LENDER_PAID },
];

export const miRenewalTypeOptions = [
  { name: 'Constant', value: MiRenewalType.CONSTANT },
  { name: 'Declining', value: MiRenewalType.DECLINING },
];

export const miCompanyTypeOptions = [
  { name: 'Enact', value: MiCompanyType.ENACT },
  { name: 'Radian', value: MiCompanyType.RADIAN },
  { name: 'Essent', value: MiCompanyType.ESSENT },
  { name: 'National MI', value: MiCompanyType.NATIONAL_MI },
  { name: 'Arch MI', value: MiCompanyType.ARCH_MI },
  { name: 'MGIC', value: MiCompanyType.MGIC },
  { name: 'Other', value: MiCompanyType.OTHER },
];

export const miApplicationTypeOptions = [
  { name: 'Delegated', value: MiApplicationType.DELEGATED },
  { name: 'Non-Delegated', value: MiApplicationType.NON_DELEGATED },
];

export const miOrderStatusOptions = [
  { name: 'Submitted', value: MiOrderStatus.SUBMITTED },
  { name: 'Decision Received', value: MiOrderStatus.DECISION_RECEIVED },
  { name: 'Activated', value: MiOrderStatus.ACTIVATED },
  { name: 'Cancelled', value: MiOrderStatus.CANCELLED },
  { name: 'Failed', value: MiOrderStatus.FAILED },
];

export const miDecisionTypeOptions = [
  { name: 'Approved', value: MiDecisionType.APPROVED },
  { name: 'Approved After Reevaluation', value: MiDecisionType.APPROVED_AFTER_REEVALUATION },
  { name: 'Conditioned Approval', value: MiDecisionType.CONDITIONED_APPROVAL },
  { name: 'Suspended', value: MiDecisionType.SUSPENDED },
  { name: 'Declined', value: MiDecisionType.DECLINED },
];

export const maritalStatusOptions = [
  { name: 'Married', value: MaritalStatusType.MARRIED },
  { name: 'Unmarried (single, divorced, widowed)', value: MaritalStatusType.UNMARRIED },
  { name: 'Separated', value: MaritalStatusType.SEPARATED },
];

const notBplCitizenshipStatusOptions = [
  { name: 'Citizen', value: CitizenshipStatusType.CITIZEN },
  { name: 'Permanent Resident Alien', value: CitizenshipStatusType.PERMANENT_RESIDENT_ALIEN },
  {
    name: 'Non-Permanent Resident Alien',
    value: CitizenshipStatusType.NON_PERMANENT_RESIDENT_ALIEN,
  },
];

export const citizenshipStatusOptions = [
  ...notBplCitizenshipStatusOptions,
  {
    name: 'Alien Foreign National',
    value: CitizenshipStatusType.ALIEN_FOREIGN_NATIONAL,
  },
];

export const incomeTypesOptions = [
  { name: 'Accessory Unit Income', value: IncomeType.ACCESSORY_UNIT_INCOME },
  { name: 'Alimony', value: IncomeType.ALIMONY },
  { name: 'Automobile Allowance', value: IncomeType.AUTOMOBILE_ALLOWANCE },
  { name: 'Boarder Income', value: IncomeType.BOARDER_INCOME },
  {
    name: 'Borrower Estimated Total Monthly Income',
    value: IncomeType.BORROWER_ESTIMATED_TOTAL_MONTHLY_INCOME,
  },
  { name: 'Capital Gains', value: IncomeType.CAPITAL_GAINS },
  { name: 'Child Support', value: IncomeType.CHILD_SUPPORT },
  { name: 'Defined Contribution Plan', value: IncomeType.DEFINED_CONTRIBUTION_PLAN },
  { name: 'Disability', value: IncomeType.DISABILITY },
  { name: 'Dividends Interest', value: IncomeType.DIVIDENDS_INTEREST },
  { name: 'Employment Related Account', value: IncomeType.EMPLOYMENT_RELATED_ACCOUNT },
  { name: 'Foster Care', value: IncomeType.FOSTER_CARE },
  {
    name: 'Housing Allowance',
    value: IncomeType.HOUSING_ALLOWANCE,
  },
  { name: 'Housing Choice Voucher Program', value: IncomeType.HOUSING_CHOICE_VOUCHER_PROGRAM },
  { name: 'Housing Or Parsonage', value: IncomeType.HOUSING_OR_PARSONAGE },
  {
    name: 'Miscellaneous Income',
    value: IncomeType.MISCELLANEOUS_INCOME,
  },
  { name: 'Mortgage Credit Certificate', value: IncomeType.MORTGAGE_CREDIT_CERTIFICATE },
  { name: 'Mortgage Differential Payments', value: IncomeType.MORTGAGE_DIFFERENTIAL_PAYMENTS },
  { name: 'Non Borrower Contribution', value: IncomeType.NON_BORROWER_CONTRIBUTION },
  { name: 'Notes Receivable', value: IncomeType.NOTES_RECEIVABLE },
  {
    name: 'Pension',
    value: IncomeType.PENSION,
  },
  { name: 'Public Assistance', value: IncomeType.PUBLIC_ASSISTANCE },
  { name: 'Retirement', value: IncomeType.RETIREMENT },
  { name: 'Royalty Payments', value: IncomeType.ROYALTY_PAYMENTS },
  { name: 'Separate Maintenance', value: IncomeType.SEPARATE_MAINTENANCE },
  { name: 'Self Employed', value: IncomeType.SELF_EMPLOYED },
  { name: 'Social Security', value: IncomeType.SOCIAL_SECURITY },
  {
    name: 'Temporary Leave',
    value: IncomeType.TEMPORARY_LEAVE,
  },
  { name: 'Tip Income', value: IncomeType.TIP_INCOME },
  { name: 'Trailing Co-Borrower Income', value: IncomeType.TRAILING_CO_BORROWER_INCOME },
  { name: 'Trust', value: IncomeType.TRUST },
  { name: 'Unemployment Benefits', value: IncomeType.UNEMPLOYMENT_BENEFITS },
  { name: 'VA Benefits Non Educational', value: IncomeType.VA_BENEFITS_NON_EDUCATIONAL },
  { name: 'VA Compensation', value: IncomeType.VA_COMPENSATION },
  { name: 'Other', value: IncomeType.OTHER },
  { name: 'W2', value: IncomeType.W2 },
];

export const employerPhoneSourceOptions = [
  { name: 'Employer Official Website', value: EmployerPhoneSource.EMPLOYER_OFFICIAL_WEBSITE },
  { name: 'Online Business Directory', value: EmployerPhoneSource.ONLINE_BUSINESS_DIRECTORY },
  {
    name: 'Third Party Verification Vendor',
    value: EmployerPhoneSource.THIRD_PARTY_VERIFICATION_VENDOR,
  },
  { name: 'Public Business Registry', value: EmployerPhoneSource.PUBLIC_BUSINESS_REGISTRY },
  {
    name: 'Employer Provided Documentation',
    value: EmployerPhoneSource.EMPLOYER_PROVIDED_DOCUMENTATION,
  },
  {
    name: 'Previous VOE or Company Record on File',
    value: EmployerPhoneSource.PREVIOUS_VOE_OR_COMPANY_RECORD_ON_FILE,
  },
  {
    name: 'Borrower Provided Information',
    value: EmployerPhoneSource.BORROWER_PROVIDED_INFORMATION,
  },
  { name: 'LinkedIn Company Profile', value: EmployerPhoneSource.LINKED_IN_COMPANY_PROFILE },
  { name: 'Other', value: EmployerPhoneSource.OTHER },
];

export const verificationMethodOptions = [
  { name: 'Verbal', value: VerificationMethod.VERBAL },
  { name: 'Email', value: VerificationMethod.EMAIL },
  { name: 'Vendor', value: VerificationMethod.VENDOR },
  { name: 'Other', value: VerificationMethod.OTHER },
];

export const employedAtTimeOfVerificationOptions = [
  { name: 'Yes', value: EmployedAtTimeOfVerification.YES },
  { name: 'No', value: EmployedAtTimeOfVerification.NO },
  { name: 'Verify Later', value: EmployedAtTimeOfVerification.VERIFY_LATER },
];

export const probabilityOfContinuedEmploymentOptions = [
  { name: 'Good', value: ProbabilityOfContinuedEmployment.GOOD },
  { name: 'Fair', value: ProbabilityOfContinuedEmployment.FAIR },
  { name: 'Refuses to Provide', value: ProbabilityOfContinuedEmployment.REFUSES_TO_PROVIDE },
];

export const businessVerificationMethodOptions = [
  { name: 'Directory', value: BusinessVerificationMethod.DIRECTORY },
  { name: 'CPA', value: BusinessVerificationMethod.CPA },
  { name: 'License', value: BusinessVerificationMethod.LICENSE },
  { name: 'Other', value: BusinessVerificationMethod.OTHER },
];

export const incomeTypeMilitaryOptions = [
  { name: 'Military Base Pay', value: IncomeType.MILITARY_BASE_PAY },
  {
    name: 'Military Clothes Allowance',
    value: IncomeType.MILITARY_CLOTHES_ALLOWANCE,
  },
  { name: 'Military Combat Pay', value: IncomeType.MILITARY_COMBAT_PAY },
  { name: 'Military Flight Pay', value: IncomeType.MILITARY_FLIGHT_PAY },
  {
    name: 'Military Hazard Pay',
    value: IncomeType.MILITARY_HAZARD_PAY,
  },
  { name: 'Military Overseas Pay', value: IncomeType.MILITARY_OVERSEAS_PAY },
  { name: 'Military Prop Pay', value: IncomeType.MILITARY_PROP_PAY },
  {
    name: 'Military Quarters Allowance',
    value: IncomeType.MILITARY_QUARTERS_ALLOWANCE,
  },
  { name: 'Military Rations Allowance', value: IncomeType.MILITARY_RATIONS_ALLOWANCE },
  {
    name: 'Military Variable Housing Allowance',
    value: IncomeType.MILITARY_VARIABLE_HOUSING_ALLOWANCE,
  },
];

export const incomeMap = _.keyBy([...incomeTypesOptions, ...incomeTypeMilitaryOptions], 'value');

export const taxFilingTypeOptions = [
  {
    name: 'Schedule C (Form 1040) — Sole Proprietorship',
    value: TaxFilingType.FORM_1040_SCHEDULE_C,
  },
  { name: '1065 — Partnership / Multi Member LLC', value: TaxFilingType.FORM_1065 },
  { name: '1120S — S Corporation', value: TaxFilingType.FORM_1120S },
  { name: '1120 — C Corporation', value: TaxFilingType.FORM_1120 },
];

export const titlePer1065TaxReturnOptions = [
  { name: 'Partner/Limited Partner', value: TitlePerTaxReturnType.PARTNER_OR_LIMITED_PARTNER },
];

export const titlePer1120TaxReturnOptions = [
  { name: 'President', value: TitlePerTaxReturnType.PRESIDENT },
  { name: 'Vice President', value: TitlePerTaxReturnType.VICE_PRESIDENT },
  { name: 'Secretary', value: TitlePerTaxReturnType.SECRETARY },
  { name: 'Treasurer', value: TitlePerTaxReturnType.TREASURER },
  { name: 'Assistant Treasurer', value: TitlePerTaxReturnType.ASSISTANT_TREASURER },
  { name: 'Chief Accountant, Officer', value: TitlePerTaxReturnType.CHIEF_ACCOUNTANT_AND_MANAGER },
];

export const titlePer1120sTaxReturnOptions = [
  { name: 'Managing Member', value: TitlePerTaxReturnType.MANAGING_MEMBER },
];

export const statesOptions = states.map(([name, value]) => ({
  name,
  value,
}));

export const statesWithCombinedName = states.map(([name, value]) => ({
  name: `${name} (${value})`,
  value,
}));

export const statesWithNationalCategoryOptions = allStatesWithNationalCategory.map(
  ([name, value]) => ({
    name,
    value,
  }),
);

export const buyDownTypeOptions = [{ name: 'Temporary', value: 'Temporary' }];

export const buyDownFunderOptions = [{ name: 'Borrower', value: 'Borrower' }];

export const assetsTypesOptions = _.sortBy(
  [
    { name: 'Checking', value: AssetsType.CHECKING },
    { name: 'Savings', value: AssetsType.SAVINGS },
    { name: 'Money Market', value: AssetsType.MONEY_MARKET },
    { name: 'Certificate of Deposit', value: AssetsType.CERTIFICATE_OF_DEPOSIT },
    { name: 'Mutual Fund', value: AssetsType.MUTUAL_FUND },
    { name: 'Stocks', value: AssetsType.STOCKS },
    { name: 'Stock Options', value: AssetsType.STOCK_OPTIONS },
    { name: 'Bonds', value: AssetsType.BONDS },
    { name: 'Retirement (401k, IRA)', value: AssetsType.RETIREMENT },
    { name: 'Bridge Loan Proceeds', value: AssetsType.BRIDGE_LOAN_PROCEEDS },
    { name: 'Individual Development Account', value: AssetsType.INDIVIDUAL_DEVELOPMENT_ACCOUNT },
    { name: 'Trust Account', value: AssetsType.TRUST_ACCOUNT },
    { name: 'Cash Value of Life Insurance', value: AssetsType.CASH_VALUE_OF_LIFE_INSURANCE },
    { name: 'Gift', value: AssetsType.GIFT },
    { name: 'Gift Of Property Equity', value: AssetsType.GIFT_OF_PROPERTY_EQUITY },
    {
      name: 'Proceeds from Real Estate Property to be sold on or before closing',
      value: AssetsType.PROCEEDS_FROM_REAL_ESTATE,
    },
    { name: 'Proceeds from Sale of Non-Real Estate Asset', value: AssetsType.PROCEEDS_FROM_SALE },
    { name: 'Secured Borrowed Funds', value: AssetsType.SECURED_BORROWED_FUNDS },
    { name: 'Unsecured Borrowed Funds', value: AssetsType.UNSECURED_BORROWED_FUNDS },
    { name: 'Other', value: AssetsType.OTHER },
    { name: 'Other: Non-Liquid', value: AssetsType.OTHER_NON_LUQUID },
    { name: 'Relocation Funds', value: AssetsType.RELOCATION_FUNDS },
    { name: 'Rent Credit', value: AssetsType.RENT_CREDIT },
    { name: 'Grant', value: AssetsType.GRANT },
  ],
  'name',
);

export const assetsObsoleteTypesOptions = [
  { name: 'Earnest Money', value: AssetsType.EARNEST_MONEY },
  { name: 'Employer Assistance', value: AssetsType.EMPLOYER_ASSISTANCE },
  { name: 'Lot Equity', value: AssetsType.LOT_EQUITY },
  { name: 'Sweat Equity', value: AssetsType.SWEAT_EQUITY },
  { name: 'Trade Equity', value: AssetsType.TRADE_EQUITY },
];

export const assetsMap = _.keyBy([...assetsTypesOptions, ...assetsObsoleteTypesOptions], 'value');

export const housingTypeOptions = [
  { name: 'Own', value: HousingType.OWN },
  { name: 'Rent', value: HousingType.RENT },
  { name: 'Living with Relative', value: HousingType.LIVING_WITH_RELATIVE },
];

export const assetsRelationshipOptions = [
  { name: 'Relative', value: AssetsRelationshipType.RELATIVE },
  { name: 'Community Nonprofit', value: AssetsRelationshipType.COMMUNITY_NON_PROFIT },
  { name: 'Federal Agency', value: AssetsRelationshipType.FEDERAL_AGENCY },
  { name: 'State Agency', value: AssetsRelationshipType.STATE_AGENCY },
  { name: 'Lender', value: AssetsRelationshipType.LENDER },
  { name: 'Employer', value: AssetsRelationshipType.EMPLOYER },
  { name: 'Local Agency', value: AssetsRelationshipType.LOCAL_AGENCY },
  { name: 'Religious Nonprofit', value: AssetsRelationshipType.RELIGIOUS_NON_PROFIT },
  { name: 'Unmarried Partner', value: AssetsRelationshipType.UNMARRIED_PARTNER },
  { name: 'Other', value: AssetsRelationshipType.OTHER },
];

export const liabilitiesTypesOptions = [
  { name: 'Liability', value: LiabilitiesType.LIABILITY },
  { name: 'Other Liabilities & Expenses', value: LiabilitiesType.OTHER },
];

export const baseStatusOptions = [
  { name: 'Yes', value: BaseStatus.YES, pinned: PinnedPosition.TOP },
  { name: 'No', value: BaseStatus.NO, pinned: PinnedPosition.TOP },
];

export const boolBaseStatusOptions = [
  { name: 'Yes', value: true, pinned: PinnedPosition.TOP },
  { name: 'No', value: false, pinned: PinnedPosition.TOP },
];

export const stateOptions = states.map(([name, value]) => ({ name, value }));

export const statesWithMilitaryAddressesOptions = allStatesWithMilitaryAddresses.map(
  ([name, value]) => ({
    name,
    value,
  }),
);

export const stateOptionsAbbreviated = states.map(([, value]) => ({ name: value, value }));

export const stateOptionsWithCodeInName = states.map(([stateName, stateCode]) => ({
  name: `${stateName}, ${stateCode}`,
  value: stateCode,
}));

export const dependentsOptions = [{ value: 0, name: 'None' }].concat(
  _.range(1, MONTH_OPTIONS_UPPER_LIMIT).map(item => ({ value: item, name: `${item}` })),
);

export const noneOption = { name: 'None', value: null };

export const suffixOptions = [
  { ...noneOption, pinned: PinnedPosition.TOP },
  { name: 'Jr.', value: SuffixType.JR, pinned: PinnedPosition.TOP },
  { name: 'Sr.', value: SuffixType.SR, pinned: PinnedPosition.TOP },
  { name: 'II', value: SuffixType.II, pinned: PinnedPosition.TOP },
  { name: 'III', value: SuffixType.III, pinned: PinnedPosition.TOP },
  { name: 'IV', value: SuffixType.IV, pinned: PinnedPosition.TOP },
];

export const previewFormCoborrowersOptions = _.range(0, MAX_COBORROWER_NUMBER + 1).map(item => ({
  value: item,
  name: `${item}`,
}));

export const previewFormNonborrowersOptions = _.range(0, MAX_NONBORROWER_NUMBER + 1).map(item => ({
  value: item,
  name: `${item}`,
}));

export const formApplicationTypeOptions = [
  { name: 'Loan Application', value: FormApplicationType.LOAN_APPLICATION },
  { name: 'Subcompany Application', value: FormApplicationType.BROKER_APPLICATION },
];

export const disclosureTypeOptions = [
  { name: 'Loan Estimate', value: DisclosureType.LOAN_ESTIMATE },
  { name: 'Closing Disclosure', value: DisclosureType.CLOSING_DISCLOSURE },
];

export const formPurposeTypeOptions = [
  { name: 'Recording', value: FormPurposeType.RECORDING },
  { name: 'Tax Authorization', value: FormPurposeType.TAX_AUTHORIZATION },
];

const taxAuthorizationFormPurposeOptions = [
  { name: 'IRS Form 8821', value: FormPurpose.IRS_FORM_8821 },
  { name: 'IRS Form 8821 – Borrower 1', value: FormPurpose.IRS_FORM_8821_BORROWER_1 },
  { name: 'IRS Form 8821 – Borrower 2', value: FormPurpose.IRS_FORM_8821_BORROWER_2 },
  { name: 'IRS Form 8821 – Borrower 3', value: FormPurpose.IRS_FORM_8821_BORROWER_3 },
  { name: 'IRS Form 8821 – Borrower 4', value: FormPurpose.IRS_FORM_8821_BORROWER_4 },
  {
    name: 'Halcyon Consent 1177 – Borrower 1',
    value: FormPurpose.HALCYON_CONSENT_1177_BORROWER_1,
  },
  {
    name: 'Halcyon Consent 1177 – Borrower 2',
    value: FormPurpose.HALCYON_CONSENT_1177_BORROWER_2,
  },
  {
    name: 'Halcyon Consent 1177 – Borrower 3',
    value: FormPurpose.HALCYON_CONSENT_1177_BORROWER_3,
  },
  {
    name: 'Halcyon Consent 1177 – Borrower 4',
    value: FormPurpose.HALCYON_CONSENT_1177_BORROWER_4,
  },
];

const recordingFormPurposeOptions = [
  { name: 'Security Instrument', value: FormPurpose.SECURITY_INSTRUMENT },
  { name: 'Rider', value: FormPurpose.RIDER },
  { name: 'Exhibit', value: FormPurpose.EXHIBIT },
  { name: 'Subordinate', value: FormPurpose.SUBORDINATE },
];

export const formPurposeConfigByType = {
  [FormPurposeType.RECORDING]: {
    label: 'Recording type',
    options: recordingFormPurposeOptions,
  },
  [FormPurposeType.TAX_AUTHORIZATION]: {
    label: 'Tax form',
    options: taxAuthorizationFormPurposeOptions,
  },
};

export const conventionalProgramLoanTypeOption = {
  name: 'Conventional',
  value: LoanType.CONVENTIONAL,
};

// The loan-type option lists all derive from the canonical PROGRAM_LOAN_TYPES catalog
// (constants/programType), so labels, values, and membership stay in one place.

const optimalBlueLoanTypeOptions = buildLoanTypeOptions([
  LoanType.CONVENTIONAL,
  LoanType.CONFORMING,
  LoanType.FHA,
  LoanType.VA,
  LoanType.NON_CONFORMING,
]);

// Everything except Chattel — the types selectable when creating/pricing a program.
export const programLoanTypeOptions = buildLoanTypeOptions(
  PROGRAM_LOAN_TYPES.map(({ value }) => value).filter(value => value !== LoanType.CHATTEL),
);

export const composedLoanTypeOptions = [...programLoanTypeOptions, ...optimalBlueLoanTypeOptions];

// Loan types an AI guideline source can be scoped to. Matches the backend
// ProgramLoanType enum exactly (Chattel is supported, DSCR is not), so values
// posted to /guidelines/sources are never rejected.
export const guidelineLoanTypeOptions = buildLoanTypeOptions(
  PROGRAM_LOAN_TYPES.map(({ value }) => value).filter(value => value !== LoanType.DSCR),
);

export const minimumInitialDrawTypeOptions = [
  { name: 'Percentage of Line Limit', value: MinimumInitialDrawType.LINE_LIMIT_PERCENTAGE },
  { name: 'Dollar Amount', value: MinimumInitialDrawType.CURRENCY_AMOUNT },
];

export const commonPropertyTypeOption = [
  { name: 'Condo', value: PropertyType.CONDO },
  { name: 'Co-op', value: PropertyType.COOP },
  { name: 'Multi-unit', value: PropertyType.MULTI_UNIT },
  { name: 'Single Family', value: PropertyType.SINGLE_FAMILY },
  { name: 'Townhouse', value: PropertyType.TOWNHOUSE },
  { name: 'Manufactured Home', value: PropertyType.MANUFACTURED_HOME },
];

export const sourceTypeOptions = [
  { name: 'Borrower', value: SourceTypeTypes.BORROWER },
  { name: 'Builder', value: SourceTypeTypes.BUILDER },
  { name: 'Community Non Profit', value: SourceTypeTypes.COMMUNITY_NON_PROFIT },
  { name: 'Employer', value: SourceTypeTypes.EMPLOYER },
  { name: 'Federal Agency', value: SourceTypeTypes.FEDERAL_AGENCY },
  { name: 'Institutional', value: SourceTypeTypes.INSTITUTIONAL },
  { name: 'Lender', value: SourceTypeTypes.LENDER },
  { name: 'Local Agency', value: SourceTypeTypes.LOCAL_AGENCY },
  {
    name: 'Non Originating Financial Institution',
    value: SourceTypeTypes.NON_ORIGINATING_FINANCIAL_INSTITUTION,
  },
  { name: 'Non Parent Relative', value: SourceTypeTypes.NON_PARENT_RELATIVE },
  { name: 'Other', value: SourceTypeTypes.OTHER },
  { name: 'Parent', value: SourceTypeTypes.PARENT },
  { name: 'Property Seller', value: SourceTypeTypes.PROPERTY_SELLER },
  { name: 'Relative', value: SourceTypeTypes.RELATIVE },
  { name: 'Religious Non Profit', value: SourceTypeTypes.RELIGIOUS_NON_PROFIT },
  { name: 'Rural Housing Service', value: SourceTypeTypes.RURAL_HOUSING_SERVICE },
  { name: 'State Agency', value: SourceTypeTypes.STATE_AGENCY },
  { name: 'Unknown', value: SourceTypeTypes.UNKNOWN },
  { name: 'Unmarried Partner', value: SourceTypeTypes.UNMARRIED_PARTNER },
  { name: 'Unrelated Friend', value: SourceTypeTypes.UNRELATED_FRIEND },
];

export const propertyTypeOptions = [
  ...commonPropertyTypeOption,
  { name: 'PUD', value: PropertyType.PUD },
];

export const propertyTypeOptionsMap = _.keyBy(propertyTypeOptions, 'value');

export const reoPropertyTypeOptions = [
  ...commonPropertyTypeOption,
  { name: 'Land', value: PropertyType.LAND },
];

export const manufacturedHomeTypeOptions = [
  { name: 'Single-Wide', value: ManufacturedHomeType.SINGLE_WIDE },
  { name: 'Double-Wide', value: ManufacturedHomeType.DOUBLE_WIDE },
  { name: 'Triple-Wide', value: ManufacturedHomeType.TRIPLE_WIDE },
];

// The Pricing Workbench prices only Single-Wide vs Double-Wide (the PPE maps Triple-Wide to
// Double-Wide), so its sub-type select drops Triple-Wide from the canonical list.
export const pricingWorkbenchManufacturedHomeTypeOptions = manufacturedHomeTypeOptions.filter(
  ({ value }) => value !== ManufacturedHomeType.TRIPLE_WIDE,
);

// The Manufactured Home Details tab's "Sections" dropdown reuses the ManufacturedHomeType column
// (a double-wide is two sections), with shorter section-count labels than manufacturedHomeTypeOptions.
export const homeSectionsOptions = [
  { name: 'Single', value: ManufacturedHomeType.SINGLE_WIDE },
  { name: 'Double', value: ManufacturedHomeType.DOUBLE_WIDE },
  { name: 'Triple', value: ManufacturedHomeType.TRIPLE_WIDE },
];

export const sitePlacementOptions = [
  { name: 'Owned / No Lien', value: SitePlacement.OWNED_NO_LIEN },
  { name: 'Leased Private Property', value: SitePlacement.LEASED_PRIVATE_PROPERTY },
  { name: 'Family Land, No Rent', value: SitePlacement.FAMILY_LAND_NO_RENT },
  { name: 'Community / Park', value: SitePlacement.COMMUNITY_PARK },
  { name: 'Reservation', value: SitePlacement.RESERVATION },
  { name: 'Owned / Land Contract', value: SitePlacement.OWNED_LAND_CONTRACT },
];

export const purposeOfChattelOptions = [
  { name: 'Chattel Purchase', value: PurposeOfChattel.PURCHASE },
  { name: 'Chattel Refinance', value: PurposeOfChattel.REFINANCE },
];

export const chattelHomeConditionOptions = [
  { name: 'New', value: ChattelHomeCondition.NEW },
  { name: 'Used', value: ChattelHomeCondition.USED },
];

export const chattelValueSourceOptions = [
  { name: 'Appraisal', value: ChattelValueSource.APPRAISAL },
  { name: 'NADA', value: ChattelValueSource.NADA },
  { name: 'Other', value: ChattelValueSource.OTHER },
];

export const pricingWorkbenchPropertyTypeOptions = [
  { name: 'Condo', value: PricingWorkbenchPropertyType.CONDO },
  { name: 'Co-op', value: PricingWorkbenchPropertyType.COOP },
  { name: 'Multi-unit 2', value: PricingWorkbenchPropertyType.MULTI_UNIT2 },
  { name: 'Multi-unit 3', value: PricingWorkbenchPropertyType.MULTI_UNIT3 },
  { name: 'Multi-unit 4', value: PricingWorkbenchPropertyType.MULTI_UNIT4 },
  { name: 'Single Family', value: PricingWorkbenchPropertyType.SINGLE_FAMILY },
  { name: 'Townhouse', value: PricingWorkbenchPropertyType.TOWNHOUSE },
  { name: 'PUD', value: PricingWorkbenchPropertyType.PUD },
  { name: 'Manufactured Home', value: PricingWorkbenchPropertyType.MANUFACTURED_HOME },
];

export const mannerHeldNotBplTypeOptions = [
  { name: 'I do not know', value: MannerHeldType.I_DO_NOT_KNOW },
  { name: 'Joint Tenancy with Right of Survivorship', value: MannerHeldType.JOINT_TENANCY },
  { name: 'Life Estate', value: MannerHeldType.LIFE_ESTATE },
  { name: 'Other', value: MannerHeldType.OTHER },
  { name: 'Sole Ownership', value: MannerHeldType.SOLE_OWNERSHIP },
  { name: 'Tenancy in Common (TIC)', value: MannerHeldType.TENANCY_IN_COMMON },
  { name: 'Tenants by Entirely', value: MannerHeldType.TENANTS_BY_ENTIRELY },
];

export const mannerHeldBplTypeOptions = [
  { name: 'Corporation', value: MannerHeldType.CORPORATION },
  { name: 'I do not know', value: MannerHeldType.I_DO_NOT_KNOW },
  { name: 'Individual', value: MannerHeldType.INDIVIDUAL },
  { name: 'Joint Tenants', value: MannerHeldType.JOINT_TENANCY },
  { name: 'LLC', value: MannerHeldType.LLC },
  { name: 'Tenants In Common', value: MannerHeldType.TENANCY_IN_COMMON },
  { name: 'Trust', value: MannerHeldType.TRUST },
];

export const leSectionOptions = [
  { name: 'Origination Charges', value: LESection.ORIGINATION_CHARGES },
  { name: 'Services You Cannot Shop For', value: LESection.SERVICES_YOU_CANNOT_SHOP_FOR },
  { name: 'Services You Can Shop For', value: LESection.SERVICES_YOU_CAN_SHOP_FOR },
  { name: 'Recording Fees', value: LESection.RECORDING_FEES },
  { name: 'Transfer Taxes', value: LESection.TRANSFER_TAXES },
  { name: 'Prepaids', value: LESection.PREPAIDS },
  { name: 'Other', value: LESection.OTHER },
];

export const leSectionRestrictedOptions = leSectionOptions.map(({ name, value }) => ({
  name: `${LESectionCDType[value]}: ${name}`,
  value,
}));

export const occupancyOptions = [
  { name: 'Investment Property', value: OccupancyType.INVESTMENT_PROPERTY },
  { name: 'Primary Residence', value: OccupancyType.PRIMARY_RESIDENCE },
  { name: 'Second Home', value: OccupancyType.SECOND_HOME },
];

export const occupancyTypeOptions = [...occupancyOptions, { name: 'Other', value: 'Other' }];

export const occupancyWithSecondaryResidenceOptions = [
  ...occupancyOptions,
  { name: 'FHA Secondary residence', value: 'SecondaryResidence' },
];

export const occupancyMap = _.keyBy(
  [...occupancyOptions, ...occupancyWithSecondaryResidenceOptions],
  'value',
);

export const titleHoldingOptions = [
  { name: 'By Yourself', value: TitleHoldingType.BY_YOURSELF },
  { name: 'Jointly with Your Spouse', value: TitleHoldingType.WITH_YOUR_SPOUSE },
  { name: 'Jointly with Another Person', value: TitleHoldingType.WITH_ANOTHER_PERSON },
];

export const closingInTrustTypeOptions = [
  { name: 'Inter Vivos (Living) Trust', value: ClosingInTrustType.INTER_VIVOS_TRUST },
  { name: 'Land Trust', value: ClosingInTrustType.LAND_TRUST },
];

export const propertyStatusOptions = [
  { name: 'Sold', value: PropertyStatusType.SOLD },
  { name: 'Pending Sale', value: PropertyStatusType.PENDING_SALE },
  { name: 'Retained', value: PropertyStatusType.RETAINED },
];

export const propertyStatusMap = _.keyBy(propertyStatusOptions, 'value');

export const deliveryStatusOptions = [
  { name: 'Sent', value: DeliveryStatus.SENT },
  { name: 'Signed', value: DeliveryStatus.SIGNED },
  { name: 'Awaiting Signature', value: DeliveryStatus.OPENED },
  { name: 'Canceled', value: DeliveryStatus.CANCELED },
];

export const typeOfCreditOptions = [
  { name: 'Mortgage', value: TypeOfCredit.MORTGAGE },
  { name: 'Line of Credit', value: TypeOfCredit.LINE_OF_CREDIT },
  { name: 'Revolving', value: TypeOfCredit.REVOLVING },
  { name: 'Installment', value: TypeOfCredit.INSTALLMENT },
  { name: 'Lease', value: TypeOfCredit.LEASE },
  { name: 'Open 30-Day', value: TypeOfCredit.OPEN_30_DAY },
  { name: 'Collection', value: TypeOfCredit.COLLECTION },
  { name: 'Other', value: TypeOfCredit.OTHER },
];

export const liabilityLoanTypeOptions = [
  noneOption,
  { name: 'FHA', value: LoanType.FHA },
  { name: 'VA', value: LoanType.VA },
  { name: 'Conventional', value: LoanType.CONVENTIONAL },
  { name: 'USDA-RD', value: LoanType.USDA },
  { name: 'Other', value: LoanType.OTHER },
];

export const liabilityLinkedToPropertyGroupedOptions = [
  { name: 'Payoff required', value: 'toBePaidOffAtOrBeforeClosing' },
  { name: 'Exclude Payment', value: 'excludePayment' },
  { name: 'Subordinate', value: 'isSubordinate' },
];

export const liabilityLinkedToPropertyOptions = [
  { name: 'Payoff required', value: 'toBePaidOffAtOrBeforeClosing' },
  { name: 'Subordinate', value: 'isSubordinate' },
  { name: 'Exclude Payment', value: 'excludePayment' },
];

export const rentCalculatedPurposeOptions = [
  { name: 'Lease', value: RentCalculatedPurposeType.LEASE },
  { name: 'Tax Return', value: RentCalculatedPurposeType.TAX_RETURN },
];

export const liabilityExpenseTypeOptions = [
  { name: 'Alimony', value: LiabilityExpenseType.ALIMONY },
  { name: 'Child Support', value: LiabilityExpenseType.CHILD_SUPPORT },
  { name: 'Separate Maintenance', value: LiabilityExpenseType.SEPARATE_MAINTENANCE },
  { name: 'Job Related Expenses', value: LiabilityExpenseType.JOB_RELATED_EXPENSES },
  { name: 'Other', value: LiabilityExpenseType.OTHER },
];

export const typeOfCreditMap = _.keyBy(
  [...typeOfCreditOptions, ...liabilityExpenseTypeOptions],
  'value',
);

export const attachmentTypeOptions = [
  { name: 'Attached', value: AttachmentType.ATTACHED },
  { name: 'Detached', value: AttachmentType.DETACHED },
];

export const numberOfUnitsOptions = [
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
];

export const calendarPeriodOptions = [
  { name: 'Month', value: views.MONTH },
  { name: 'Week', value: views.WEEK },
];

export const taskStatusOptions = Object.values(TaskStatus).map(name => ({
  name,
  value: name,
  pinned: PinnedPosition.TOP,
}));

const sexOptions = [
  {
    value: Sex.MALE,
    name: 'Male',
  },
  {
    value: Sex.FEMALE,
    name: 'Female',
  },
];

export const demographicSexOptions = [
  ...sexOptions,
  {
    name: 'I do not wish to provide this information',
    value: SEX_NOT_PROVIDED_VALUE,
  },
];

export const sexNotProvidedOption = {
  name: 'Not Provided',
  value: SEX_NOT_PROVIDED_VALUE,
};

export const tolerantSexOptions = [...sexOptions, sexNotProvidedOption];

export const tolerantSexOptionsMap = _.keyBy(tolerantSexOptions, 'value');

export const docMagicPlanCodeOptions = [
  { name: 'DSI_CONV', value: DocMagicPlanCodeType.DSI_CONV },
  { name: 'DSI_TX6', value: DocMagicPlanCodeType.DSI_TX6 },
  { name: 'CONVNB', value: DocMagicPlanCodeType.CONVNB },
  { name: '6MOLIB', value: DocMagicPlanCodeType.SIX_MOLIB },
];

export const prepaymentPenaltyFeatureOptions = [
  { name: 'Required', value: PrepaymentPenaltyFeatureType.REQUIRED },
  { name: 'Not Offered', value: PrepaymentPenaltyFeatureType.NOT_OFFERED },
  { name: 'Optional', value: PrepaymentPenaltyFeatureType.OPTIONAL },
];

export const hispanicOrLatinoEthnicityOptions = [
  { name: 'Mexican', value: HispanicOrLatinoEthnicitySubType.MEXICAN },
  { name: 'Cuban', value: HispanicOrLatinoEthnicitySubType.CUBAN },
  { name: 'Puerto Rican', value: HispanicOrLatinoEthnicitySubType.PUERTO_RICAN },
  {
    name: 'Other Hispanic or Latino - Print origin',
    value: HispanicOrLatinoEthnicitySubType.OTHER_HISPANIC_OR_LATINO,
  },
];

export const hispanicOrLatinoEthnicityOptionsMap = _.keyBy(
  hispanicOrLatinoEthnicityOptions,
  'value',
);

export const asianRaceOptions = [
  { name: 'Asian Indian', value: AsianRaceSubType.ASIAN_INDIAN },
  { name: 'Chinese', value: AsianRaceSubType.CHINESE },
  { name: 'Filipino', value: AsianRaceSubType.FILIPINO },
  { name: 'Japanese', value: AsianRaceSubType.JAPANESE },
  { name: 'Korean', value: AsianRaceSubType.KOREAN },
  { name: 'Vietnamese', value: AsianRaceSubType.VIETNAMESE },
  { name: 'Other Asian - Print Race', value: AsianRaceSubType.OTHER_ASIAN },
];

export const asianRaceOptionsMap = _.keyBy(asianRaceOptions, 'value');

export const nativeHawaiianOrOtherRaceOptions = [
  { name: 'Native Hawaiian', value: NativeHawaiianOrOtherRaceSubType.NATIVE_HAWAIIAN },
  { name: 'Guamanian or Chamorro', value: NativeHawaiianOrOtherRaceSubType.GUAMANIAN_OR_CHAMORRO },
  { name: 'Samoan', value: NativeHawaiianOrOtherRaceSubType.SAMOAN },
  {
    name: 'Other Pacific Islander - Print race',
    value: NativeHawaiianOrOtherRaceSubType.OTHER_PACIFIC_ISLANDER,
  },
];

export const nativeHawaiianOrOtherRaceOptionsMap = _.keyBy(
  nativeHawaiianOrOtherRaceOptions,
  'value',
);

export const ethnicityOptions = [
  { name: 'Hispanic or Latino', value: EthnicityType.HISPANIC_OR_LATINO },
  { name: 'Not Hispanic or Latino', value: EthnicityType.NON_HISPANIC_OR_LATINO },
  { name: 'I do not wish to provide this information', value: EthnicityType.NOT_PROVIDED },
];

const ethnicityOptionsV2 = [
  { name: 'Hispanic or Latino', value: 'true', key: EthnicityType.HISPANIC_OR_LATINO },
  { name: 'Not Hispanic or Latino', value: 'true', key: EthnicityType.NON_HISPANIC_OR_LATINO },
  {
    name: 'I do not wish to provide this information',
    value: 'true',
    key: EthnicityType.NOT_PROVIDED,
  },
];

export const ethnicityOptionsMap = _.keyBy(ethnicityOptionsV2, 'key');

export const bankruptTypesOptions = [
  { name: 'Chapter 7', value: BankruptType.CHAPTER_7 },
  { name: 'Chapter 11', value: BankruptType.CHAPTER_11 },
  { name: 'Chapter 12', value: BankruptType.CHAPTER_12 },
  { name: 'Chapter 13', value: BankruptType.CHAPTER_13 },
];

export const demographicInformationProvisionTypeOptions = [
  {
    name: 'Face-to-Face Interview (includes Electronic Media w/ Video Component)',
    value: DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW,
  },
  {
    name: 'Telephone Interview',
    value: DemographicInformationProvisionType.TELEPHONE_INTERVIEW,
  },
  {
    name: 'Fax or Mail',
    value: DemographicInformationProvisionType.FAX_OR_MAIL,
  },
  {
    name: 'Email or Internet',
    value: DemographicInformationProvisionType.EMAIL_OR_INTERNET,
  },
];

export const originationFeeTypeOptions = [
  { name: 'Percentage of Loan Amount', value: OriginationFeeType.RATE },
  { name: 'Flat Fee', value: OriginationFeeType.FLAT },
];

export const locationTypeOptions = [
  { name: 'Suburban', value: LocationType.SUBURBAN },
  { name: 'Urban', value: LocationType.URBAN },
  { name: 'Rural', value: LocationType.RURAL },
];

export const incomeDocumentationTypeOptions = [
  { name: 'Full doc', value: IncomeDocumentationType.FULL_DOC },
  {
    name: 'Personal bank statements (12 months)',
    value: IncomeDocumentationType.PERSONAL_BANK_STATEMENT_12,
  },
  {
    name: 'Personal bank statements (24 months)',
    value: IncomeDocumentationType.PERSONAL_BANK_STATEMENT_24,
  },
  {
    name: 'Business bank statements (12 months)',
    value: IncomeDocumentationType.BUSINESS_BANK_STATEMENT_12,
  },
  {
    name: 'Business bank statements (24 months)',
    value: IncomeDocumentationType.BUSINESS_BANK_STATEMENT_24,
  },
  { name: '1099 (1 year)', value: IncomeDocumentationType.FORM_1099_ONE_YEAR },
  { name: '1099 (2 years)', value: IncomeDocumentationType.FORM_1099_TWO_YEAR },
  { name: 'Profit and loss (12 months)', value: IncomeDocumentationType.PROFIT_AND_LOSS_12 },
  { name: 'Profit and loss (24 months)', value: IncomeDocumentationType.PROFIT_AND_LOSS_24 },
  { name: 'Asset depletion', value: IncomeDocumentationType.ASSET_DEPLETION },
  { name: 'Written VOE', value: IncomeDocumentationType.WVOE },
  { name: 'DSCR', value: IncomeDocumentationType.DSCR },
  { name: 'No ratio', value: IncomeDocumentationType.NO_RATIO },
];

export const prepaymentPenaltyStructureOptions = [
  { name: 'None', value: PrepaymentPenaltyStructure.NONE },
  { name: 'Fixed', value: PrepaymentPenaltyStructure.FIXED },
  { name: 'Declining', value: PrepaymentPenaltyStructure.DECLINING },
  { name: 'Six months’ interest', value: PrepaymentPenaltyStructure.SIX_MONTHS_INTEREST },
];

// A program's Required penalty has a structure of its own; Pricing refuses None for it.
export const programPrepaymentPenaltyStructureOptions = prepaymentPenaltyStructureOptions.filter(
  ({ value }) => value !== PrepaymentPenaltyStructure.NONE,
);

export const propertyRightsEstateHeldOptions = [
  { name: 'Fee Simple', value: PropertyRightsEstateHeld.FEE_SIMPLE },
  { name: 'Leasehold', value: PropertyRightsEstateHeld.LEASE_HOLD },
];

export const entityAssignmentTypes = [
  {
    name: 'Income',
    value: EntityAssignmentType.INCOME,
  },
  {
    name: 'Asset',
    value: EntityAssignmentType.ASSET,
  },
  { name: 'Borrower', value: EntityAssignmentType.BORROWER },
];

export const primaryBorrowerEATOptions = [
  {
    name: 'Primary Borrower',
    value: null,
  },
];

export const eachBorrowerEATOptions = [
  {
    name: 'Each Borrower',
    value: null,
  },
];

export const selectedBorrowersEATOptions = [
  {
    name: 'Selected Borrowers',
    value: null,
  },
];

export const loanRolesCapacityOptions = [
  { name: 'Single', value: LoanRoleCapacity.SINGLE },
  { name: 'Multiple', value: LoanRoleCapacity.MULTIPLE },
];

export const horizontalAlignmentOptions = [
  { name: 'Left', value: HorizontalAlignmentType.LEFT },
  { name: 'Center', value: HorizontalAlignmentType.CENTER },
  { name: 'Right', value: HorizontalAlignmentType.RIGHT },
];

export const verticalAlignmentOptions = [
  { name: 'Top', value: VerticalAlignmentType.TOP },
  { name: 'Middle', value: VerticalAlignmentType.MIDDLE },
  { name: 'Bottom', value: VerticalAlignmentType.BOTTOM },
];

export const floatOptions = [
  { name: 'Left', value: 'left' },
  { name: 'Right', value: 'right' },
];

const NONE_OPTION_NAME = 'None';
const NONE_OPTION_VALUE = '';

export const dynamicFieldPrefixOptions = [
  { name: NONE_OPTION_NAME, value: NONE_OPTION_VALUE },
  { name: DynamicFieldPrefixSymbol.DOLLAR, value: DynamicFieldPrefixSymbol.DOLLAR },
];

export const dynamicFieldPostfixOptions = [
  { name: NONE_OPTION_NAME, value: NONE_OPTION_VALUE },
  { name: DynamicFieldPostfixSymbol.PERCENT, value: DynamicFieldPostfixSymbol.PERCENT },
];

export const defaultDynamicFieldPrefixOption = dynamicFieldPrefixOptions[0];
export const defaultDynamicFieldPostfixOption = dynamicFieldPostfixOptions[0];

export const customQuestionOptions = [
  { name: 'Text', value: CustomQuestionsAnswerType.TEXT },
  { name: 'Number', value: CustomQuestionsAnswerType.INTEGER },
  { name: 'Number With Decimal', value: CustomQuestionsAnswerType.DOUBLE },
  { name: 'Yes/No', value: CustomQuestionsAnswerType.BOOL },
];

export const borrowerHomeownershipEducationFormatOptions = [
  {
    name: 'Attended Workshop In Person',
    value: BorrowerHomeownershipEducationFormat.ATTENDED_WORKSHOP_IN_PERSON,
  },
  {
    name: 'Completed Web Based Workshop',
    value: BorrowerHomeownershipEducationFormat.COMPLETED_WEB_BASED_WORKSHOP,
  },
];

export const borrowerHousingCounselingFormatOptions = [
  {
    name: 'Face To Face',
    value: BorrowerHousingCounselingFormat.FACE_TO_FACE,
  },
  {
    name: 'Telephone',
    value: BorrowerHousingCounselingFormat.TELEPHONE,
  },
  {
    name: 'Internet',
    value: BorrowerHousingCounselingFormat.INTERNET,
  },
  {
    name: 'Hybrid',
    value: BorrowerHousingCounselingFormat.HYBRID,
  },
];

// TODO: replace languagePreferenceOptionsV2 with languagePreferenceOptions after 1003/declarations will be fully deleted
export const languagePreferenceOptionsV2 = [
  { name: 'English', value: LanguagePreferenceV2.ENGLISH },
  { name: 'Chinese (中文)', value: LanguagePreferenceV2.CHINESE },
  { name: 'Korean (한국어)', value: LanguagePreferenceV2.KOREAN },
  { name: 'Spanish (Español)', value: LanguagePreferenceV2.SPANISH },
  { name: 'Tagalog (Tagalog)', value: LanguagePreferenceV2.TAGALOG },
  { name: 'Vietnamese (Tiếng Việt)', value: LanguagePreferenceV2.VIETNAMESE },
  { name: 'I do not wish to respond', value: LanguagePreferenceV2.I_DO_NOT_WITH_TO_RESPOND },
  { name: 'Other', value: LanguagePreferenceV2.OTHER },
];

export const companyTypeSelectOptions = [
  { label: 'Lender', value: CompanyType.LENDER },
  { label: 'Broker', value: CompanyType.BROKER },
];

export const organizationTypeOptions = [
  { name: 'Corporation', value: OrganizationType.CORPORATION },
  { name: 'Limited Liability Company', value: OrganizationType.LIMITED_LIABILITY_COMPANY },
  { name: 'Limited Partnership', value: OrganizationType.LIMITED_PARTNERSHIP },
  { name: 'Limited Liability Partnership', value: OrganizationType.LIMITED_LIABILITY_PARTNERSHIP },
  { name: 'Partnership', value: OrganizationType.PARTNERSHIP },
  { name: 'Federal Savings Bank', value: OrganizationType.FEDERAL_SAVINGS_BANK },
  { name: 'State Savings Bank', value: OrganizationType.STATE_SAVINGS_BANK },
  { name: 'Savings and Loan Association', value: OrganizationType.SAVINGS_LOAN_ASSOSIATION },
  { name: 'Credit Union', value: OrganizationType.CREDIT_UNION },
];

export const hmdaFederalAgencyCodeOptions = [
  { name: '1 - OCC (Office of the Comptroller of the Currency)', value: HmdaFederalAgencyCode.OCC },
  { name: '2 - FRS (Federal Reserve System)', value: HmdaFederalAgencyCode.FRS },
  { name: '3 - FDIC (Federal Deposit Insurance Corporation)', value: HmdaFederalAgencyCode.FDIC },
  { name: '5 - NCUA (National Credit Union Administration)', value: HmdaFederalAgencyCode.NCUA },
  {
    name: '7 - HUD (Department of Housing and Urban Development)',
    value: HmdaFederalAgencyCode.HUD,
  },
  { name: '9 - CFPB (Consumer Financial Protection Bureau)', value: HmdaFederalAgencyCode.CFPB },
];

export const subCompanyTypeOptions = [
  { name: 'Broker', value: SubCompanyType.BROKER },
  { name: 'Correspondent', value: SubCompanyType.CORRESPONDENT },
  { name: 'Branch', value: SubCompanyType.BRANCH },
];

// Display labels are the customer-facing credit bands (Excellent → Bad); the enum values keep the
// legacy wire tokens, so POOR ('Poor') is the ≤639 "Bad" band. Key order = display order. Single
// source of the band labels; mirrors Realtors' CREDIT_SCORE_TO_LABEL_MAP.
const creditRatingLabels = {
  [CreditRatingStatus.EXCELLENT]: 'Excellent',
  [CreditRatingStatus.GOOD]: 'Good',
  [CreditRatingStatus.AVERAGE]: 'Fair',
  [CreditRatingStatus.BELOW_AVERAGE]: 'Poor',
  [CreditRatingStatus.POOR]: 'Bad',
};

export const creditRatingOptions = Object.entries(creditRatingLabels).map(([value, name]) => ({
  name,
  value,
}));

export const executionTypeOptions = [
  { name: 'Best Ex', value: EXECUTION_TYPE.BEST_EX },
  { name: 'Cash', value: EXECUTION_TYPE.CASH },
  { name: 'MBS', value: EXECUTION_TYPE.MBS },
];

export const loanFeePaymentTimeOptions = [
  {
    name: 'At Closing',
    value: LoanFeePaymentTimeEnum.AT_CLOSING,
  },
  {
    name: 'Before Closing',
    value: LoanFeePaymentTimeEnum.BEFORE_CLOSING,
  },
];

export const closingLocationOptions = [
  { name: 'Primary Residence', value: ClosingLocation.PRIMARY_RESIDENCE },
  { name: 'Title/Escrow Company', value: ClosingLocation.TITLE_ESCROW_COMPANY },
];

export const monthsOptions = _.range(1, MONTH_OPTIONS_COUNT).map(value => ({
  name: `${value}`,
  value,
}));

export const monthsWithZeroOptions = _.range(0, MONTH_OPTIONS_COUNT).map(value => ({
  name: `${value}`,
  value,
}));

export const projectStatusTypeOptions = [
  {
    name: 'Established',
    value: ProjectInformation.ESTABLISHED,
  },
  {
    name: 'New',
    value: ProjectInformation.NEW,
  },
];

export const projectClassificationOptions = [
  {
    name: 'PUD (E)',
    value: ProjectClassification.PUD_E,
  },
  {
    name: 'PUD (F)',
    value: ProjectClassification.PUD_F,
  },
  {
    name: 'PUD (T)',
    value: ProjectClassification.PUD_T,
  },
  {
    name: 'CO-OP (1)',
    value: ProjectClassification.CO_OP_1,
  },
  {
    name: 'CO-OP (2)',
    value: ProjectClassification.CO_OP_2,
  },
  {
    name: 'CO-OP (T)',
    value: ProjectClassification.CO_OP_T,
  },
];

export const uccpAppraisalUploadStatusOptions = [
  {
    name: 'In Progress',
    value: UccpAppraisalUploadStatus.IN_PROGRESS,
  },
  {
    name: 'Successful',
    value: UccpAppraisalUploadStatus.SUCCESSFUL,
  },
  {
    name: 'Not Successful',
    value: UccpAppraisalUploadStatus.NOT_SUCCESSFUL,
  },
  {
    name: 'Rejected',
    value: UccpAppraisalUploadStatus.REJECTED,
  },
  {
    name: 'N/A',
    value: UccpAppraisalUploadStatus.NA,
  },
];

export const impoundCategoryOptions = [
  { name: 'Property Taxes', value: ImpoundCategoryType.PROPERTY_TAXES },
  { name: 'Supplemental Insurance', value: ImpoundCategoryType.SUPPLEMENTAL_INSURANCE },
];

export const creditOptions = [
  { name: 'Lender Credit', value: CreditTypeEnum.LENDER_CREDIT },
  { name: 'Seller Credit', value: CreditTypeEnum.SELLER_CREDIT },
  { name: 'Purchase Credit', value: CreditTypeEnum.PURCHASE_CREDIT },
  { name: 'Other Credit', value: CreditTypeEnum.OTHER_CREDIT },
  { name: 'FHA MI Credit', value: CreditTypeEnum.FHA_MI_CREDIT },
];

export const creditTypeOptionsMap = _.keyBy(creditOptions, 'value');

export const purchaseCreditTypeOptions = [
  { name: 'Earnest Money', value: PurchaseCreditType.EARNEST_MONEY },
  { name: 'Employer Assisted Housing', value: PurchaseCreditType.EMPLOYER_ASSISTED_HOUSING },
  { name: 'Lease Purchase Fund', value: PurchaseCreditType.LEASE_PURCHASE_FUND },
  { name: 'Sweat Equity', value: PurchaseCreditType.SWEAT_EQUITY },
  {
    name: 'Trade Equity From Property Swap',
    value: PurchaseCreditType.TRADE_EQUITY_FROM_PROPERTY_SWAP,
  },
  { name: 'Closing Costs', value: PurchaseCreditType.CLOSING_COSTS },
  { name: 'Lot Equity', value: PurchaseCreditType.LOT_EQUITY },
  { name: 'Relocation Funds', value: PurchaseCreditType.RELOCATION_FUNDS },
];

export const purchaseCreditTypeOptionsMap = _.keyBy(purchaseCreditTypeOptions, 'value');

export const purchaseCreditSourceTypeOptions = [
  {
    name: 'Borrower Paid Outside Closing',
    value: PurchaseCreditSourceType.BORROWER_PAID_OUTSIDE_CLOSING,
  },
  { name: 'Builder Developer', value: PurchaseCreditSourceType.BUILDER_DEVELOPER },
  { name: 'Employer', value: PurchaseCreditSourceType.EMPLOYER },
  { name: 'Federal Agency', value: PurchaseCreditSourceType.FEDERAL_AGENCY },
  { name: 'Lender', value: PurchaseCreditSourceType.LENDER },
  { name: 'Local Agency', value: PurchaseCreditSourceType.LOCAL_AGENCY },
  { name: 'Non Parent Relative', value: PurchaseCreditSourceType.NON_PARENT_RELATIVE },
  { name: 'Employer Affiliate', value: PurchaseCreditSourceType.EMPLOYER_AFFILIATE },
  { name: 'Lender Affiliate', value: PurchaseCreditSourceType.LENDER_AFFILIATE },
  { name: 'Parent', value: PurchaseCreditSourceType.PARENT },
  { name: 'Real Estate Agent', value: PurchaseCreditSourceType.REAL_ESTATE_AGENT },
  { name: 'State Agency', value: PurchaseCreditSourceType.STATE_AGENCY },
  { name: 'Unrelated Friend', value: PurchaseCreditSourceType.UNRELATED_FRIEND },
  { name: 'Other', value: PurchaseCreditSourceType.OTHER },
];

export const purchaseCreditSourceTypeOptionsMap = _.keyBy(purchaseCreditSourceTypeOptions, 'value');

export const frequencyOptions = [
  { name: 'Annual', value: FrequencyType.ANNUAL, secondaryValue: 1 },
  { name: 'Biannual', value: FrequencyType.BIANNUAL, secondaryValue: 2 },
  { name: 'Triannual', value: FrequencyType.TRIANNUAL, secondaryValue: 3 },
  { name: 'Quarterly', value: FrequencyType.QUATERLY, secondaryValue: 4 },
  { name: 'Monthly', value: FrequencyType.MONTHLY, secondaryValue: 12 },
];

export const fundingInformationTypeOptions = [
  { name: 'Wet', value: FundingInformationType.WET },
  { name: 'Dry', value: FundingInformationType.DRY },
];

export const closingDocumentTypeOptions = [
  {
    name: 'Recorded Mortgage + Riders',
    value: ClosingDocumentType.SHIP_RECORDED_MORTGAGE_PLUS_RIDERS,
  },
  { name: 'Final Title Policy', value: ClosingDocumentType.SHIP_FINAL_TITLE_POLICY },
];

export const feeProviderPaidToTypeOptions = [
  { name: 'Broker', value: FeeProviderPaidToType.BROKER },
  { name: 'Broker Affiliate', value: FeeProviderPaidToType.BROKER_AFFILIATE },
  { name: 'HUD', value: FeeProviderPaidToType.HUD },
  { name: 'Investor', value: FeeProviderPaidToType.INVESTOR },
  { name: 'Lender', value: FeeProviderPaidToType.LENDER },
  { name: 'Lender Affiliate', value: FeeProviderPaidToType.LENDER_AFFILIATE },
  { name: 'Other', value: FeeProviderPaidToType.OTHER },
  { name: 'Seller', value: FeeProviderPaidToType.SELLER },
  { name: 'Service Provider', value: FeeProviderPaidToType.SERVICE_PROVIDER },
  { name: 'USDA', value: FeeProviderPaidToType.USDA },
  { name: 'VA', value: FeeProviderPaidToType.VA },
];

export const loanContactTypesOptions = [
  {
    name: 'Borrower 1 (Primary Borrower)',
    value: LoanContactTypeEnum.Borrower1,
    isWithoutTag: true,
  },
  { name: 'Borrower 2 (Co-Borrower)', value: LoanContactTypeEnum.Borrower2, isWithoutTag: true },
  { name: 'Borrower 3 (Co-Borrower)', value: LoanContactTypeEnum.Borrower3, isWithoutTag: true },
  { name: 'Borrower 4 (Co-Borrower)', value: LoanContactTypeEnum.Borrower4, isWithoutTag: true },
  { name: 'Realtor', value: LoanContactTypeEnum.Realtor, isWithoutTag: true },
  {
    name: 'Non Borrowing Entities',
    value: LoanContactTypeEnum.NonBorrowingEntities,
    isWithoutTag: true,
  },
  {
    name: 'Selling Agents',
    value: LoanContactTypeEnum.SellingAgents,
    isWithoutTag: true,
  },
  {
    name: 'Listing Agents',
    value: LoanContactTypeEnum.ListingAgents,
    isWithoutTag: true,
  },
  {
    name: 'Sellers',
    value: LoanContactTypeEnum.Sellers,
    isWithoutTag: true,
  },
  {
    name: 'Non-Borrower Entity 1',
    value: LoanContactTypeEnum.NonBorrowerEntity1,
    isWithoutTag: true,
  },
  {
    name: 'Non-Borrower Entity 2',
    value: LoanContactTypeEnum.NonBorrowerEntity2,
    isWithoutTag: true,
  },
  {
    name: 'Non-Borrower Entity 3',
    value: LoanContactTypeEnum.NonBorrowerEntity3,
    isWithoutTag: true,
  },
  {
    name: 'Non-Borrower Entity 4',
    value: LoanContactTypeEnum.NonBorrowerEntity4,
    isWithoutTag: true,
  },
  {
    name: 'Vested Individual 1',
    value: LoanContactTypeEnum.VestedIndividual1,
    isWithoutTag: true,
  },
  {
    name: 'Vested Individual 2',
    value: LoanContactTypeEnum.VestedIndividual2,
    isWithoutTag: true,
  },
  {
    name: 'Vested Individual 3',
    value: LoanContactTypeEnum.VestedIndividual3,
    isWithoutTag: true,
  },
  {
    name: 'Vested Individual 4',
    value: LoanContactTypeEnum.VestedIndividual4,
    isWithoutTag: true,
  },
  {
    name: 'Title Company Contact',
    value: LoanContactTypeEnum.TitleCompanyContact,
    isWithoutTag: true,
  },
];

export const loanSenderTypesOptions = [
  { name: 'System Address', value: LoanContactTypeEnum.SystemAddress, isWithoutTag: true },
];

export const ppeTypesOptions = [
  { name: 'Lender PPE', value: PpeType.LENDER_PPE },
  { name: 'Lender PPE 2.0', value: PpeType.LENDER_V2_PPE },
  { name: 'Optimal Blue PPE', value: PpeType.OPTIMAL_BLUE_PPE },
  { name: 'Optimal Blue - OB UI', value: PpeType.OPTIMAL_BLUE_UI_PPE },
  { name: 'Loan Sifter PPE', value: PpeType.LOAN_SIFTER_PPE },
  { name: 'No PPE', value: PpeType.NO_PPE },
];

export const rateTypeOptions = [
  { name: 'Fixed', value: RateType.FIXED },
  { name: 'Variable', value: RateType.VARIABLE },
];

export const amortizationTypeOptions = [
  { name: 'Interest Only', value: AmortizationType.INTEREST_ONLY },
  { name: 'Fully Amortized', value: AmortizationType.FULLY_AMORTIZED },
];

export const loanProgramAmortizationTypeOptions = [
  { name: 'Fixed', value: LoanProgramAmortizationType.FIXED },
  { name: 'ARM', value: LoanProgramAmortizationType.ARM },
];

export const deedRestrictedOptions = [
  { name: 'Income Based Deed Restrictions Survive', value: DeedRestrictionType.SURVIVE },
  { name: 'Income Based Deed Restrictions Terminate', value: DeedRestrictionType.TERMINATE },
];

export const nationalHousingActSectionOptions = [
  {
    name: 'Section 203(b): Federal mortgage insurance to finance homeownership and the construction and financing of housing',
    value: NationalHousingActSection.SECTION_203B_FEDERAL_MORTGAGE,
  },
  {
    name: 'Sections 203(b)/251: Condominium/ARM',
    value: NationalHousingActSection.SECTION_203B251_CONDOMINIUM_ARM,
  },
  {
    name: 'Section 203(k): FHA Rehabilitation Loan Insurance. insures loan to finance the rehabilitation or purchase and rehabilitation of on-to-four-family properties',
    value: NationalHousingActSection.SECTION_203K_FHA_REHABILITATION_LOAN_INSURANCE,
  },
  {
    name: 'Sections 203(k)/251: Purchase, Rehab/ARM',
    value: NationalHousingActSection.SECTION_203K251_PURCHASE_REHAB_ARM,
  },
  {
    name: 'Section 234(c): FHA mortgage Insurance for Condominium Units. Federal mortgage insurance to finance the purchase of individual housing units in proposed or existing condominiums',
    value: NationalHousingActSection.SECTION_234C_FHA_MORTGAGE_INSURANCE_FOR_CONDOMINIUM_UNITS,
  },
  {
    name: 'Sections 234(c)/251: Condominium/ARM',
    value: NationalHousingActSection.SECTION_234C251_CONDOMINIUM_ARM,
  },
];

export const governmentRefinancePurposeOptions = [
  //order is important
  {
    name: 'Full Documentation',
    value: GovernmentRefinancePurposeType.FULL_DOCUMENTATION,
  },
  {
    name: 'Interest Rate Reduction Refinance Loan',
    value: GovernmentRefinancePurposeType.INTEREST_RATE_REDUCTION_REFINANCE_LOAN,
  },
  {
    name: 'Streamline Without Appraisal',
    value: GovernmentRefinancePurposeType.STREAM_LINE_WITHOUT_APPRAISAL,
  },
];

export const documentVerificationAssetTypeOptions = [
  noneOption,
  { value: 'Verified', name: 'Verified' },
  { value: 'Stated', name: 'Stated' },
];

export const documentVerificationEmploymentTypeOptions = [
  noneOption,
  { value: 'Verified', name: 'Verified' },
  { value: 'Stated', name: 'Stated' },
];

export const documentVerificationIncomeTypeOptions = [
  noneOption,
  { value: 'Verified', name: 'Verified' },
  { value: 'StatedWithIRSForm', name: 'Stated w/ IRS Form' },
  { value: 'StatedWithoutIRSForm', name: 'Stated w/o IRS Form' },
];

export const salaryOptions = [
  {
    name: 'Base Salary',
    value: SalaryType.BASE_SALARY,
  },
  {
    name: 'Contract Basis',
    value: SalaryType.CONTRACT_BASIS,
  },
  {
    name: 'Monthly Bonus',
    value: SalaryType.MONTHLY_BONUS,
  },
  {
    name: 'Monthly Commission',
    value: SalaryType.MONTHLY_COMMISSION,
  },
  {
    name: 'Monthly Overtime',
    value: SalaryType.MONTHLY_OVERTIME,
  },
  {
    name: 'Monthly Other Income',
    value: SalaryType.MONTHLY_OTHER,
  },
  {
    name: 'Military Base Pay',
    value: SalaryType.MILITARY_BASE_PAY,
  },
  {
    name: 'Military Clothes Allowance',
    value: SalaryType.MILITARY_CLOTHES_ALLOWANCE,
  },
  {
    name: 'Military Combat Pay',
    value: SalaryType.MILITARY_COMBAT_PAY,
  },
  {
    name: 'Military Flight Pay',
    value: SalaryType.MILITARY_FLIGHT_PAY,
  },
  {
    name: 'Military Hazard Pay',
    value: SalaryType.MILITARY_HAZARD_PAY,
  },
  {
    name: 'Military Other',
    value: SalaryType.MILITARY_OTHER,
  },
  {
    name: 'Military Overseas Pay',
    value: SalaryType.MILITARY_OVERSEAS_PAY,
  },
  {
    name: 'Military Prop Pay',
    value: SalaryType.MILITARY_PROP_PAY,
  },
  {
    name: 'Military Rations Allowance',
    value: SalaryType.MILITARY_RATIONS_ALLOWANCE,
  },
  {
    name: 'Military Variable Housing Allowance',
    value: SalaryType.MILITARY_VARIABLE_HOUSING_ALLOWANCE,
  },
  {
    name: 'Military Quarters Allowance',
    value: SalaryType.MILITARY_QUARTERS_ALLOWANCE,
  },
];

export const salaryMap = _.keyBy(salaryOptions, 'value');

export const ucdTypeOptions = [
  {
    name: 'Fannie May UCD',
    value: UcdSubmitType.UCD,
  },
  {
    name: 'Freddie Mac LCA',
    value: UcdSubmitType.LCA,
  },
];

export const mismoDocumentTypeOptions = [
  {
    name: 'Standard MISMO (origination)',
    value: MismoDocumentType.DEFAULT,
  },
  {
    name: 'UCD Closing Disclosure',
    value: MismoDocumentType.UCD,
  },
];

export const FANNIE_SYSTEM_OPTIONS = [
  { value: FANNIE_SYSTEM_TYPE.DO, name: 'DO' },
  { value: FANNIE_SYSTEM_TYPE.DU, name: 'DU' },
];

export const AUS_ROLE_TYPE_OPTIONS = [
  { name: 'Correspondent Lender', value: 'CorrespondentLender' },
  { name: 'Loan Seller', value: 'LoanSeller' },
  { name: 'Mortgage Broker', value: 'MortgageBroker' },
];

export const downPaymentSourceOptions = [
  { name: 'Certificate Of Deposit', value: DownPaymentSourceType.CERTIFICATE_OF_DEPOSIT },
  { name: 'Checking', value: DownPaymentSourceType.CHECKING },
  { name: 'Gift', value: DownPaymentSourceType.GIFT },
  { name: 'Retirement Account', value: DownPaymentSourceType.RETIREMENT_ACCOUNT },
  { name: 'Savings', value: DownPaymentSourceType.SAVINGS },
  { name: 'Stocks Bonds Mutual Funds', value: DownPaymentSourceType.STOCKS_BONDS_MUTUAL_FUNDS },
  { name: 'Other', value: DownPaymentSourceType.OTHER },
];

export const federalAgencyComplianceOptions = [
  {
    name: 'Consumer Financial Protection Bureau (CFPB)',
    value: FederalAgencyComplianceType.CONSUMER_FINANCIAL_PROTECTION_BUREAU,
  },
  {
    name: 'Federal Deposit Insurance Corporation (FDIC)',
    value: FederalAgencyComplianceType.FEDERAL_DEPOSIT_INSURANCE_CORPORATION,
  },
  {
    name: 'National Credit Union Administration (NCUA)',
    value: FederalAgencyComplianceType.NATIONAL_CREDIT_UNION_ADMINISTRATION,
  },
  {
    name: 'Office of the Comptroller of the Currency (OCC)',
    value: FederalAgencyComplianceType.OFFICE_OF_THE_COMPTROLLER,
  },
];

export const formPopulationOptions = [
  { name: 'May have Prepayment Penalty', value: FormPopulationOption.MAY_HAVE_PREPAYMENT_PENALTY },
  { name: 'Will not have to pay a penalty', value: FormPopulationOption.WILL_NOT_HAVE_TO_PAY },
  {
    name: 'Will not be entitled to a refund of part of the finance charge',
    value: FormPopulationOption.WILL_NOT_BE_ENTITLED_TO_REFUND,
  },
];

export const assumableDetailsOptions = [
  { name: 'May', value: AssumableDetailsOption.MAY },
  { name: 'May, subject to conditions', value: AssumableDetailsOption.MAY_SUBJECT_TO_CONDITIONS },
  {
    name: 'May not assume the remainder of your loan on the original terms',
    value: AssumableDetailsOption.MAY_NOT_ASSUME_REMAINDER_OF_LOAN,
  },
];

export const servicingOptions = [
  {
    name: 'We intend to service your loan. If so, you make your payments to us.',
    value: ServicingType.SERVICE_LOAN,
  },
  { name: 'We intend to transfer servicing of your loan.', value: ServicingType.TRANSFER },
];

export const loanNumberSegmentTypeOptions = [
  { name: 'Company prefix', value: LoanNumberSegmentType.COMPANY_PREFIX },
  { name: 'Year (YY)', value: LoanNumberSegmentType.YEAR },
  { name: 'Sequence counter', value: LoanNumberSegmentType.SEQUENCE_COUNTER },
  { name: 'Static text', value: LoanNumberSegmentType.STATIC_TEXT },
];

export const loanNumberResetBehaviorOptions = [
  { name: 'Reset each year', value: LoanNumberResetBehavior.RESET_EACH_YEAR },
  { name: 'Run continuously', value: LoanNumberResetBehavior.RUN_CONTINUOUSLY },
];

export const escrowRequirementOptions = [
  { name: 'Not Allowed', value: EscrowRequirementType.NOT_ALLOWED },
  { name: 'Optional', value: EscrowRequirementType.OPTIONAL },
  { name: 'Required', value: EscrowRequirementType.REQUIRED },
];

export const sortingOrderOptions = [
  { name: 'ASC', value: SORT_DIRECTIONS.ASC },
  { name: 'DESC', value: SORT_DIRECTIONS.DESC },
];

export const entitlementCodeFromCoeOptions = [
  {
    name: '01 - Regular',
    value: EntitlementCodeFromCoeType.REGULAR,
  },
  {
    name: '02 - Reserves / National Guard',
    value: EntitlementCodeFromCoeType.RESERVERS_NATIONAL_GUARD,
  },
  {
    name: '03 - Surviving Spouse (Active Duty Death)',
    value: EntitlementCodeFromCoeType.SURVIVING_SPOUSE_ACTIVE_DUTY_DEATH,
  },
  {
    name: '04 - Surviving Spouse (Service-Connected Death)',
    value: EntitlementCodeFromCoeType.SURVIVING_SPOUSE_SERVICE_CONNECTED_DEATH,
  },
  {
    name: '05 - Active Duty',
    value: EntitlementCodeFromCoeType.ACTIVE_DUTY,
  },
  {
    name: '06 - Unremarried Surviving Spouse of POW/MIA',
    value: EntitlementCodeFromCoeType.UNREMARRIED_SURVIVING_SPOUSE_OF_POW_MIA,
  },
  {
    name: '07 - Spouse of Active Duty Service Member',
    value: EntitlementCodeFromCoeType.SPOUSE_OF_ACTIVE_DUTY_SERVICE_MEMBER,
  },
  {
    name: '10 - Selected Reserve / Guard (Post-1992)',
    value: EntitlementCodeFromCoeType.SELECTED_RESERVE_GUARD_POST_1992,
  },
  {
    name: '11 - Other',
    value: EntitlementCodeFromCoeType.OTHER,
  },
];

export const branchOfServiceOptions = [
  {
    name: 'Army',
    value: BranchOfServiceType.ARMY,
  },
  {
    name: 'Navy',
    value: BranchOfServiceType.NAVY,
  },
  {
    name: 'Air Force',
    value: BranchOfServiceType.AIR_FORCE,
  },
  {
    name: 'Marines',
    value: BranchOfServiceType.MARINES,
  },
  {
    name: 'Coast Guard',
    value: BranchOfServiceType.COAST_GUARD,
  },
  {
    name: 'Space Force',
    value: BranchOfServiceType.SPACE_FORCE,
  },
];

export const militaryStatusOptions = [
  {
    name: 'Active',
    value: MilitaryStatusType.ACTIVE,
  },
  {
    name: 'Reserve/Guard',
    value: MilitaryStatusType.RESERVE_GUARD,
  },
  {
    name: 'Veteran',
    value: MilitaryStatusType.VETERAN,
  },
  {
    name: 'Surviving Spouse',
    value: MilitaryStatusType.SURVING_SPOUSE,
  },
];

export const defaultFontFamilyOption = {
  name: 'Auto',
  value: 0,
};

export const fontFamilyOptions = [
  defaultFontFamilyOption,
  {
    name: 'Axiforma Black Italic',
    value: 'AxiformaBlackItalic',
  },
  {
    name: 'Axiforma Black',
    value: 'AxiformaBlack',
  },
  {
    name: 'Axiforma Bold Italic',
    value: 'AxiformaBoldItalic',
  },
  {
    name: 'Axiforma Bold',
    value: 'AxiformaBold',
  },
  {
    name: 'Axiforma Book Italic',
    value: 'AxiformaBookItalic',
  },
  {
    name: 'Axiforma Book',
    value: 'AxiformaBook',
  },
  {
    name: 'Axiforma ExtraBold Italic',
    value: 'AxiformaExtraBoldItalic',
  },
  {
    name: 'Axiforma ExtraBold',
    value: 'AxiformaExtraBold',
  },
  {
    name: 'Axiforma Heavy Italic',
    value: 'AxiformaHeavyItalic',
  },
  {
    name: 'Axiforma Heavy',
    value: 'AxiformaHeavy',
  },
  {
    name: 'Axiforma Italic',
    value: 'AxiformaItalic',
  },
  {
    name: 'Axiforma Light Italic',
    value: 'AxiformaLightItalic',
  },
  {
    name: 'Axiforma Light',
    value: 'AxiformaLight',
  },
  {
    name: 'Axiforma Medium Italic',
    value: 'AxiformaMediumItalic',
  },
  {
    name: 'Axiforma Medium',
    value: 'AxiformaMedium',
  },
  {
    name: 'Axiforma Regular',
    value: 'AxiformaRegular',
  },
  {
    name: 'Axiforma SemiBold Italic',
    value: 'AxiformaSemiBoldItalic',
  },
  {
    name: 'Axiforma SemiBold',
    value: 'AxiformaSemiBold',
  },
  {
    name: 'Axiforma Thin Italic',
    value: 'AxiformaThinItalic',
  },
  {
    name: 'Axiforma Thin',
    value: 'AxiformaThin',
  },
  {
    name: 'Arial',
    value: 'Arial',
  },
  {
    name: 'Courier New',
    value: 'CourierNew',
  },
  {
    name: 'Georgia',
    value: 'Georgia',
  },
  {
    name: 'Lucida Sans Unicode',
    value: 'LucidaSansUnicode',
  },
  {
    name: 'Tahoma',
    value: 'Tahoma',
  },
  {
    name: 'Times New Roman',
    value: 'TimesNewRoman',
  },
  {
    name: 'Trebuchet MS',
    value: 'TrebuchetMS',
  },
  {
    name: 'Verdana',
    value: 'Verdana',
  },
];

export const fontFamilyMap = _.keyBy(fontFamilyOptions, 'value');
